import { useRef, useState, useEffect } from 'react';
import { processImageWithPixels } from '../utils/pixelProcessor';

// Debug flag - set to true to enable colored containers and console logging
const DEBUG_COLORS = false;

function PDFPreview({
  pdfFile,
  progress,
  isProcessing,
  error,
  extractedPages,
  currentProcessedPage,
  currentPageIndex,
  isProcessingCurrentPage,
  showOriginal,
  pageExceptions = {},
  onToggleShowOriginal, // New prop for toggling show original
  // Navigation props
  onFirstPage,
  onPreviousPage,
  onNextPage,
  onLastPage,
  totalPages,
  // Live preview props
  processedPagesCache = {},
  getEffectiveFilters,
  filters = {}
}) {
  const containerRef = useRef(null);
  const [containerHeight, setContainerHeight] = useState(0);
  const [previewProcessedImage, setPreviewProcessedImage] = useState(null);
  const [isProcessingPreview, setIsProcessingPreview] = useState(false);

  // Get the current page mode label and styling
  const getPageModeInfo = () => {
    const pageNumber = currentPageIndex + 1;
    const exception = pageExceptions[pageNumber];

    if (!exception || !exception.enabled) {
      return { label: 'Default', color: 'bg-gray-500 dark:bg-gray-600' };
    }

    if (exception.preserveOriginal) {
      return { label: 'Preserve', color: 'bg-amber-500 dark:bg-amber-600' };
    }

    if (exception.useGlobal) {
      return { label: 'Global', color: 'bg-blue-500 dark:bg-blue-600' };
    }

    // Check if it has a preset label
    if (exception.label && exception.label !== 'Custom' && exception.label !== 'Global Settings') {
      return { label: `Preset: ${exception.label}`, color: 'bg-purple-500 dark:bg-purple-600' };
    }

    return { label: 'Custom', color: 'bg-indigo-500 dark:bg-indigo-600' };
  };

  const getPhaseText = (phase) => {
    switch (phase) {
      case 'extracting':
        return 'Extracting pages';
      case 'processing':
        return 'Processing pages';
      case 'generating':
        return 'Generating PDF';
      default:
        return 'Processing';
    }
  };

  const currentExtractedPage = extractedPages[currentPageIndex];
  const pageNumber = currentPageIndex + 1;

  // Get effective filters for current page (includes preview filters if set)
  const effectiveFilters = getEffectiveFilters
    ? getEffectiveFilters(pageNumber)
    : filters;

  console.log('[PDFPreview] Page:', pageNumber, 'Effective filters:', effectiveFilters);

  // Debug: Log when preview image state changes
  useEffect(() => {
    console.log('[PDFPreview] 🖼️ previewProcessedImage state changed:', previewProcessedImage ? 'HAS IMAGE' : 'NULL');
    if (previewProcessedImage) {
      console.log('[PDFPreview] 🖼️ Preview image length:', previewProcessedImage.length);
    }
  }, [previewProcessedImage]);

  // Determine which processed image to display
  // Priority: preview-processed > cached-processed > currentProcessedPage
  const cachedProcessed = processedPagesCache?.[pageNumber];
  let displayImage = null;
  let isShowingPreview = false;

  // Check if effective filters differ from base filters (indicating preview mode)
  const filtersChanged = getEffectiveFilters && JSON.stringify(effectiveFilters) !== JSON.stringify(filters);

  console.log('[PDFPreview] Display logic - previewProcessedImage:', previewProcessedImage ? 'EXISTS' : 'NULL');
  console.log('[PDFPreview] Display logic - filtersChanged:', filtersChanged);
  console.log('[PDFPreview] Display logic - isProcessingPreview:', isProcessingPreview);

  if (previewProcessedImage) {
    // Use live preview if available
    displayImage = previewProcessedImage;
    isShowingPreview = true;
    console.log('[PDFPreview] ✅ Using live preview image');
  } else if (filtersChanged && cachedProcessed?.processedDataUrl) {
    // Filters changed but preview not ready yet, use cached as fallback
    displayImage = cachedProcessed.processedDataUrl;
    isShowingPreview = false;
    console.log('[PDFPreview] ⏳ Filters changed, preview processing, using cached image temporarily');
  } else if (cachedProcessed?.processedDataUrl) {
    displayImage = cachedProcessed.processedDataUrl;
    console.log('[PDFPreview] 📦 Using cached processed image');
  } else if (currentProcessedPage?.processedDataUrl) {
    displayImage = currentProcessedPage.processedDataUrl;
    console.log('[PDFPreview] 📄 Using currentProcessedPage');
  }

  // Measure container height on mount and when window resizes
  useEffect(() => {
    const updateHeight = () => {
      if (containerRef.current) {
        const height = containerRef.current.clientHeight;
        setContainerHeight(height);
        console.log('📏 PDFPreview - Container height updated:', height, 'on page:', currentPageIndex + 1);
      }
    };

    // Immediate update
    updateHeight();

    window.addEventListener('resize', updateHeight);

    // Multiple delayed updates to catch layout shifts at different times
    const timeoutId1 = setTimeout(updateHeight, 50);
    const timeoutId2 = setTimeout(updateHeight, 150);
    const timeoutId3 = setTimeout(updateHeight, 300);

    return () => {
      window.removeEventListener('resize', updateHeight);
      clearTimeout(timeoutId1);
      clearTimeout(timeoutId2);
      clearTimeout(timeoutId3);
    };
  }, [showOriginal, currentPageIndex]);

  // Process live preview when filters change
  useEffect(() => {
    let isCancelled = false;

    const processPreview = async () => {
      // Only process if filters have changed and we have an original image
      if (!filtersChanged || !currentExtractedPage?.imageDataUrl) {
        console.log('[PDFPreview] Skipping preview - filtersChanged:', filtersChanged, 'hasImage:', !!currentExtractedPage?.imageDataUrl);
        setPreviewProcessedImage(null);
        setIsProcessingPreview(false);
        return;
      }

      console.log('[PDFPreview] Filters changed, processing live preview for page', pageNumber);
      console.log('[PDFPreview] Effective filters:', effectiveFilters);
      setIsProcessingPreview(true);

      try {
        const processedDataUrl = await processImageWithPixels(
          currentExtractedPage.imageDataUrl,
          effectiveFilters
        );

        if (!isCancelled) {
          console.log('[PDFPreview] Live preview processed successfully');
          console.log('[PDFPreview] Setting previewProcessedImage to:', processedDataUrl ? 'valid data URL' : 'null');
          console.log('[PDFPreview] Preview image length:', processedDataUrl?.length);
          setPreviewProcessedImage(processedDataUrl);
          setIsProcessingPreview(false);
          console.log('[PDFPreview] State updates queued, should trigger re-render');
        }
      } catch (error) {
        console.error('[PDFPreview] Error processing live preview:', error);
        if (!isCancelled) {
          setPreviewProcessedImage(null);
          setIsProcessingPreview(false);
        }
      }
    };

    processPreview();

    return () => {
      isCancelled = true;
    };
  }, [filtersChanged, currentExtractedPage?.imageDataUrl, JSON.stringify(effectiveFilters), pageNumber]);

  // Get current container height (fallback to measurement if ref available)
  const getCurrentHeight = () => {
    if (containerRef.current) {
      const height = containerRef.current.clientHeight;
      console.log('🔍 getCurrentHeight - measured:', height, 'state:', containerHeight);
      return height;
    }
    console.log('🔍 getCurrentHeight - using fallback:', containerHeight || 600);
    return containerHeight || 600; // Fallback height
  };

  // Calculate dimensions for images based on available height
  const calculateImageDimensions = (imageWidth, imageHeight, availableHeight) => {
    if (!imageWidth || !imageHeight || !availableHeight) {
      return { width: 'auto', height: 'auto' };
    }

    const aspectRatio = imageWidth / imageHeight;
    // Badge is py-1.5 (12px) + text (~16px) + mb-2 (8px) = ~36px, but be aggressive
    const maxHeight = availableHeight - 40; // Just enough for badge
    const maxWidth = (containerRef.current?.clientWidth || 800) - 16; // Small padding for borders

    // Start by maximizing height usage
    let displayHeight = maxHeight;
    let displayWidth = displayHeight * aspectRatio;

    // If width exceeds container, scale down from width instead
    if (displayWidth > maxWidth) {
      displayWidth = maxWidth;
      displayHeight = displayWidth / aspectRatio;
    }

    const result = {
      width: Math.floor(displayWidth),
      height: Math.floor(displayHeight)
    };

    console.log('📐 calculateImageDimensions:', {
      originalSize: `${imageWidth}×${imageHeight}`,
      aspectRatio: aspectRatio.toFixed(2),
      availableHeight,
      maxHeight,
      maxWidth,
      calculatedSize: `${result.width}×${result.height}`,
      utilizationPercent: `${((result.height / availableHeight) * 100).toFixed(1)}%`
    });

    return result;
  };

  // Determine optimal layout for Show Original mode (vertical vs side-by-side)
  const determineOptimalLayout = (pageWidth, pageHeight) => {
    if (!containerRef.current || !pageWidth || !pageHeight) {
      return 'vertical'; // Default to vertical if we can't calculate
    }

    const containerWidth = containerRef.current.clientWidth;
    const containerHeight = getCurrentHeight();
    const padding = 32; // p-4 total padding
    const gap = 12; // gap-3
    const badgeHeight = 40; // Height reserved for badge

    // Calculate for VERTICAL layout (stacked)
    const verticalAvailableHeight = (containerHeight - padding - gap) / 2;
    const verticalMaxHeight = verticalAvailableHeight - badgeHeight;
    const verticalMaxWidth = containerWidth - padding - 16; // padding + borders

    const pageAspectRatio = pageWidth / pageHeight;
    let verticalHeight = verticalMaxHeight;
    let verticalWidth = verticalHeight * pageAspectRatio;
    if (verticalWidth > verticalMaxWidth) {
      verticalWidth = verticalMaxWidth;
      verticalHeight = verticalWidth / pageAspectRatio;
    }
    const verticalArea = verticalWidth * verticalHeight;

    // Calculate for SIDE-BY-SIDE layout
    const sideBySideAvailableWidth = (containerWidth - padding - gap) / 2;
    const sideBySideMaxHeight = containerHeight - padding - badgeHeight;
    const sideBySideMaxWidth = sideBySideAvailableWidth - 16; // padding + borders

    let sideBySideHeight = sideBySideMaxHeight;
    let sideBySideWidth = sideBySideHeight * pageAspectRatio;
    if (sideBySideWidth > sideBySideMaxWidth) {
      sideBySideWidth = sideBySideMaxWidth;
      sideBySideHeight = sideBySideWidth / pageAspectRatio;
    }
    const sideBySideArea = sideBySideWidth * sideBySideHeight;

    console.log('📊 Layout comparison:', {
      pageAspectRatio: pageAspectRatio.toFixed(2),
      vertical: { width: Math.floor(verticalWidth), height: Math.floor(verticalHeight), area: Math.floor(verticalArea) },
      sideBySide: { width: Math.floor(sideBySideWidth), height: Math.floor(sideBySideHeight), area: Math.floor(sideBySideArea) },
      chosen: sideBySideArea > verticalArea ? 'side-by-side' : 'vertical'
    });

    // Choose layout with larger image area
    return sideBySideArea > verticalArea ? 'horizontal' : 'vertical';
  };

  // Debug logging
  console.log('🔍 PDFPreview - extractedPages array:', extractedPages);
  console.log('🔍 PDFPreview - currentPageIndex:', currentPageIndex);
  console.log('🔍 PDFPreview - currentExtractedPage:', currentExtractedPage);
  console.log('🔍 PDFPreview - has imageDataUrl?:', !!currentExtractedPage?.imageDataUrl);
  console.log('🔍 PDFPreview - imageDataUrl length:', currentExtractedPage?.imageDataUrl?.length);
  console.log('🔍 PDFPreview - imageDataUrl preview:', currentExtractedPage?.imageDataUrl?.substring(0, 50));
  console.log('🔍 PDFPreview - currentProcessedPage:', currentProcessedPage);
  console.log('🔍 PDFPreview - has processedDataUrl?:', !!currentProcessedPage?.processedDataUrl);
  console.log('🔍 PDFPreview - showOriginal:', showOriginal);

  return (
    <div className="flex-1 min-h-0 h-full flex flex-col p-1 sm:p-4 md:p-6 overflow-hidden">
      {/* Error Display */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
          <p className="text-red-800 dark:text-red-300 font-semibold">Error processing PDF:</p>
          <p className="text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* Processing State */}
      {isProcessing && (
        <div className="space-y-4">
          <div className="flex items-center justify-center mb-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 dark:border-indigo-400"></div>
          </div>

          <div className="text-center">
            <p className="text-lg font-semibold text-gray-700 dark:text-gray-200 mb-2">
              {getPhaseText(progress.phase)}: Page {progress.current} of {progress.total}
            </p>
          </div>

          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
            <div
              className="bg-indigo-600 dark:bg-indigo-500 h-3 rounded-full transition-all duration-300"
              style={{
                width: `${progress.total > 0 ? (progress.current / progress.total) * 100 : 0}%`,
              }}
            />
          </div>
        </div>
      )}

      {/* Page Preview */}
      {!isProcessing && !error && extractedPages.length > 0 && currentExtractedPage && (
        <div
          className={`flex-1 flex flex-col border-2 border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden min-h-0 ${DEBUG_COLORS ? 'bg-blue-100' : 'bg-white dark:bg-gray-900'}`}
          {...(DEBUG_COLORS && {
            onClick: () => console.log('🔵 BLUE-100: Page Preview Outer Container'),
            onTouchStart: () => console.log('🔵 BLUE-100: Page Preview Outer Container')
          })}
        >
          {/* Page Header */}
          <div
            className={`px-3 md:px-4 py-1.5 md:py-3 border-b border-gray-200 dark:border-gray-600 flex items-center justify-between flex-shrink-0 gap-2 ${DEBUG_COLORS ? 'bg-blue-200' : 'bg-gray-50 dark:bg-gray-800'}`}
            {...(DEBUG_COLORS && {
              onClick: (e) => { e.stopPropagation(); console.log('🔵 BLUE-200: Page 1 of X Header'); },
              onTouchStart: (e) => { e.stopPropagation(); console.log('🔵 BLUE-200: Page 1 of X Header'); }
            })}
          >
            <p className="text-xs md:text-sm font-semibold text-gray-700 dark:text-gray-200 flex-shrink truncate">
              Page {currentExtractedPage.pageNumber} <span className="md:hidden">of {extractedPages.length}</span><span className="hidden md:inline"> • </span><span className="hidden sm:inline">{currentExtractedPage.width} × {currentExtractedPage.height} px</span>
            </p>
            <div className="flex items-center gap-2 flex-shrink-0">
              {isProcessingCurrentPage && (
                <div className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-indigo-600 dark:border-indigo-400 border-t-transparent"></div>
                  <span className="text-xs text-indigo-700 dark:text-indigo-300 hidden sm:inline">Processing...</span>
                </div>
              )}
            </div>
          </div>
          {/* Preview Content Container */}
          <div
            ref={containerRef}
            className={`flex-1 p-2 md:p-4 overflow-hidden flex flex-col ${DEBUG_COLORS ? 'bg-blue-300' : 'bg-gray-100 dark:bg-gray-900'}`}
            {...(DEBUG_COLORS && {
              onClick: (e) => { e.stopPropagation(); console.log('🔵 BLUE-300: Preview Content Area (where images show)'); },
              onTouchStart: (e) => { e.stopPropagation(); console.log('🔵 BLUE-300: Preview Content Area (where images show)'); }
            })}
          >
            {showOriginal ? (
              // Show original and final - layout determined dynamically for optimal image size
              (() => {
                const optimalLayout = determineOptimalLayout(currentExtractedPage?.width, currentExtractedPage?.height);
                const currentHeight = getCurrentHeight();
                const containerWidth = containerRef.current?.clientWidth || 800;

                if (optimalLayout === 'horizontal') {
                  // Side-by-side layout
                  console.log('📊 Show Original - Side-by-side layout, height:', currentHeight);

                  return (
                    <div className="flex flex-row h-full gap-3">
                      {/* Original Image - 50% width */}
                      <div className="flex-1 min-h-0 flex flex-col">
                        <div className="flex-1 min-h-0 flex justify-center items-center overflow-visible relative">
                          {currentExtractedPage && currentExtractedPage.imageDataUrl ? (
                            <>
                              <img
                                src={currentExtractedPage.imageDataUrl}
                                alt={`Page ${currentExtractedPage.pageNumber} - Original`}
                                className="w-full h-full object-contain border-2 border-gray-300 dark:border-gray-600 rounded shadow-lg"
                                onError={(e) => {
                                  console.error('Failed to load original image:', e);
                                  console.log('imageDataUrl:', currentExtractedPage.imageDataUrl?.substring(0, 100));
                                }}
                              />
                              {/* Top-left pill - Original */}
                              <div className="absolute top-2 left-2">
                                <span className="inline-flex items-center px-2.5 py-1 rounded-md bg-red-100 dark:bg-red-900/50 text-xs text-red-700 dark:text-red-300 font-semibold shadow-md">
                                  Original
                                </span>
                              </div>
                            </>
                          ) : (
                            <div className="text-center text-gray-500 dark:text-gray-400 flex flex-col items-center justify-center">
                              <p className="text-sm">Original not available</p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Final Image - 50% width */}
                      <div className="flex-1 min-h-0 flex flex-col">
                        <div className="flex-1 min-h-0 flex justify-center items-center overflow-visible relative">
                          {displayImage ? (
                            <>
                              <img
                                src={displayImage}
                                alt={`Page ${currentExtractedPage.pageNumber} - Processed`}
                                className={`w-full h-full object-contain border-2 border-gray-300 dark:border-gray-600 rounded shadow-lg ${isProcessingPreview ? 'opacity-70' : ''}`}
                              />
                              {/* Top-left pill - Final with processing/preview status */}
                              <div className="absolute top-2 left-2">
                                <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold shadow-md ${
                                  isProcessingPreview
                                    ? 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-700 dark:text-yellow-300'
                                    : isShowingPreview
                                    ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300'
                                    : 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300'
                                }`}>
                                  {isProcessingPreview ? 'Processing Preview...' : isShowingPreview ? 'Preview' : 'Final'}
                                </span>
                              </div>
                              {/* Processing indicator overlay */}
                              {isProcessingPreview && (
                                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-indigo-600 dark:border-indigo-400 border-t-transparent"></div>
                                </div>
                              )}
                            </>
                          ) : (
                            <div className="text-center text-gray-500 dark:text-gray-400 flex flex-col items-center justify-center">
                              <div className="animate-spin rounded-full h-8 w-8 border-2 border-indigo-600 dark:border-indigo-400 border-t-transparent mx-auto mb-2"></div>
                              <p className="text-sm">Processing...</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                } else {
                  // Vertical layout (stacked)
                  console.log('📊 Show Original - Vertical layout, height per image:', (currentHeight - 32 - 12) / 2);

                  return (
                    <div className="flex flex-col h-full gap-3">
                      {/* Original Image - 50% height */}
                      <div className="flex-1 min-h-0 flex flex-col">
                        <div className="flex-1 min-h-0 flex justify-center items-center overflow-visible relative">
                          {currentExtractedPage && currentExtractedPage.imageDataUrl ? (
                            <>
                              <img
                                src={currentExtractedPage.imageDataUrl}
                                alt={`Page ${currentExtractedPage.pageNumber} - Original`}
                                className="w-full h-full object-contain border-2 border-gray-300 dark:border-gray-600 rounded shadow-lg"
                                onError={(e) => {
                                  console.error('Failed to load original image:', e);
                                  console.log('imageDataUrl:', currentExtractedPage.imageDataUrl?.substring(0, 100));
                                }}
                              />
                              {/* Top-left pill - Original */}
                              <div className="absolute top-2 left-2">
                                <span className="inline-flex items-center px-2.5 py-1 rounded-md bg-red-100 dark:bg-red-900/50 text-xs text-red-700 dark:text-red-300 font-semibold shadow-md">
                                  Original
                                </span>
                              </div>
                            </>
                          ) : (
                            <div className="text-center text-gray-500 dark:text-gray-400 flex flex-col items-center justify-center">
                              <p className="text-sm">Original not available</p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Final Image - 50% height */}
                      <div className="flex-1 min-h-0 flex flex-col">
                        <div className="flex-1 min-h-0 flex justify-center items-center overflow-visible relative">
                          {displayImage ? (
                            <>
                              <img
                                src={displayImage}
                                alt={`Page ${currentExtractedPage.pageNumber} - Processed`}
                                className={`w-full h-full object-contain border-2 border-gray-300 dark:border-gray-600 rounded shadow-lg ${isProcessingPreview ? 'opacity-70' : ''}`}
                              />
                              {/* Top-left pill - Final with processing/preview status */}
                              <div className="absolute top-2 left-2">
                                <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold shadow-md ${
                                  isProcessingPreview
                                    ? 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-700 dark:text-yellow-300'
                                    : isShowingPreview
                                    ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300'
                                    : 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300'
                                }`}>
                                  {isProcessingPreview ? 'Processing Preview...' : isShowingPreview ? 'Preview' : 'Final'}
                                </span>
                              </div>
                              {/* Processing indicator overlay */}
                              {isProcessingPreview && (
                                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-indigo-600 dark:border-indigo-400 border-t-transparent"></div>
                                </div>
                              )}
                            </>
                          ) : (
                            <div className="text-center text-gray-500 dark:text-gray-400 flex flex-col items-center justify-center">
                              <div className="animate-spin rounded-full h-8 w-8 border-2 border-indigo-600 dark:border-indigo-400 border-t-transparent mx-auto mb-2"></div>
                              <p className="text-sm">Processing...</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                }
              })()
            ) : (
              // Show only processed page
              (() => {
                const currentHeight = getCurrentHeight();
                // Container has p-4 (32px total padding)
                const contentHeight = currentHeight - 32; // Remove padding
                console.log('📊 Single Page - currentHeight:', currentHeight, 'contentHeight:', contentHeight);

                const singleImageDimensions = calculateImageDimensions(
                  currentExtractedPage?.width,
                  currentExtractedPage?.height,
                  contentHeight
                );

                return (
                  <div className="w-full h-full flex justify-center items-center overflow-hidden relative">
                    {displayImage ? (
                      <>
                        <img
                          src={displayImage}
                          alt={`Page ${currentExtractedPage.pageNumber}`}
                          className={`max-w-full max-h-full object-contain border-2 border-gray-300 dark:border-gray-600 rounded shadow-lg ${isProcessingPreview ? 'opacity-70' : ''}`}
                        />
                        {/* Top-left pill */}
                        <div className="absolute top-2 left-2">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold shadow-md ${
                            isProcessingPreview
                              ? 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-700 dark:text-yellow-300'
                              : isShowingPreview
                              ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300'
                              : 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300'
                          }`}>
                            {isProcessingPreview ? 'Processing Preview...' : isShowingPreview ? 'Preview' : getPageModeInfo().label}
                          </span>
                        </div>
                        {/* Processing indicator overlay */}
                        {isProcessingPreview && (
                          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                            <div className="animate-spin rounded-full h-8 w-8 border-2 border-indigo-600 dark:border-indigo-400 border-t-transparent"></div>
                          </div>
                        )}
                      </>
                    ) : currentExtractedPage?.imageDataUrl ? (
                      <>
                        <img
                          src={currentExtractedPage.imageDataUrl}
                          alt={`Page ${currentExtractedPage.pageNumber} - Original`}
                          className="max-w-full max-h-full object-contain border-2 border-gray-300 dark:border-gray-600 rounded shadow-lg opacity-50"
                        />
                        <div className="absolute top-2 left-2">
                          <span className="inline-flex items-center px-2.5 py-1 rounded-md bg-yellow-100 dark:bg-yellow-900/50 text-xs text-yellow-700 dark:text-yellow-300 font-semibold shadow-md">
                            Processing...
                          </span>
                        </div>
                      </>
                    ) : (
                      <div className="text-center text-gray-500 dark:text-gray-400">
                        <div className="animate-spin rounded-full h-8 w-8 border-2 border-indigo-600 dark:border-indigo-400 border-t-transparent mx-auto mb-2"></div>
                        <p className="text-sm">Processing page with filters...</p>
                      </div>
                    )}
                  </div>
                );
              })()
            )}
          </div>

          {/* Navigation Footer - Show on all screen sizes */}
          {onFirstPage && onPreviousPage && onNextPage && onLastPage && (
            <div
              className={`px-3 py-1.5 border-t border-gray-200 dark:border-gray-600 flex-shrink-0 ${DEBUG_COLORS ? 'bg-blue-400' : 'bg-gray-50 dark:bg-gray-800'}`}
              {...(DEBUG_COLORS && {
                onClick: (e) => { e.stopPropagation(); console.log('🔵 BLUE-400: Navigation Buttons Footer'); },
                onTouchStart: (e) => { e.stopPropagation(); console.log('🔵 BLUE-400: Navigation Buttons Footer'); }
              })}
            >
              <div className="grid grid-cols-4 gap-1.5">
                <button
                  onClick={onFirstPage}
                  disabled={currentPageIndex === 0}
                  className="px-2 py-1.5 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                  title="First page"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18.41 16.59L13.82 12l4.59-4.59L17 6l-6 6 6 6 1.41-1.41zM6 6h2v12H6V6z" />
                  </svg>
                </button>
                <button
                  onClick={onPreviousPage}
                  disabled={currentPageIndex === 0}
                  className="px-2 py-1.5 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                  title="Previous page"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12l4.58-4.59z" />
                  </svg>
                </button>
                <button
                  onClick={onNextPage}
                  disabled={currentPageIndex === (totalPages || extractedPages.length) - 1}
                  className="px-2 py-1.5 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                  title="Next page"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6-6-6z" />
                  </svg>
                </button>
                <button
                  onClick={onLastPage}
                  disabled={currentPageIndex === (totalPages || extractedPages.length) - 1}
                  className="px-2 py-1.5 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                  title="Last page"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M5.59 7.41L10.18 12l-4.59 4.59L7 18l6-6-6-6-1.41 1.41zM16 6h2v12h-2V6z" />
                  </svg>
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default PDFPreview;
